import { useCallback, useEffect, useRef, useState } from 'react';

import { fetchSatellites } from './apiClient';
import { enrichSatellites } from './satelliteEnrich';
import { propagateCatalogRaw, propagateOrbitPoints } from './satellitePropagate';
import { ISS_NORAD_ID } from '../satellite/satelliteGlobeConstants';
import type { RichSatellite, Satellite, SatellitePositionRaw, SatellitesResponse } from './types';

const PRIMARY_GROUP = 'active';
const FALLBACK_GROUPS = [
  'starlink',
  'stations',
  'gps-ops',
  'glo-ops',
  'galileo',
  'beidou',
  'geo',
  'science',
  'military',
  'debris',
] as const;

const PROPAGATE_MS = 1000;
const CATALOG_POLL_MS = 120_000;
const IDB_NAME = 'flightscanner-sat-catalog';
const IDB_STORE = 'catalog';

export interface SatelliteCatalogState {
  data: SatellitesResponse | null;
  satellites: RichSatellite[];
  catalog: Satellite[];
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  catalogAgeSec: number | null;
  refresh: () => void;
  fetchOrbit: (noradId: string) => Promise<Array<[number, number, number]>>;
}

function apiErrorMessage(err: unknown): string {
  const msg = String(err);
  if (msg.includes('404')) return 'Satellite API route not found';
  if (msg.includes('429')) return 'Satellite data rate limited';
  if (msg.includes('503')) return 'Satellite catalog temporarily unavailable';
  if (msg.includes('timed out')) return `${msg}. Try again shortly.`;
  return (err as Error).message || 'Failed to load satellites';
}

async function readIdbCache(group: string): Promise<{ fetchedAt: number; catalog: Satellite[]; stale?: boolean } | null> {
  if (typeof indexedDB === 'undefined') return null;
  try {
    const db = await new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(IDB_NAME, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(IDB_STORE);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    const row = await new Promise<{ fetchedAt: number; catalog: Satellite[] } | undefined>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const req = tx.objectStore(IDB_STORE).get(group);
      req.onsuccess = () => resolve(req.result as { fetchedAt: number; catalog: Satellite[] } | undefined);
      req.onerror = () => reject(req.error);
    });
    db.close();
    if (!row) return null;
    const ageMs = Date.now() - row.fetchedAt * 1000;
    return ageMs > 7200_000 ? { ...row, stale: true } : row;
  } catch {
    return null;
  }
}

async function writeIdbCache(group: string, fetchedAt: number, catalog: Satellite[]) {
  if (typeof indexedDB === 'undefined') return;
  try {
    const db = await new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(IDB_NAME, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(IDB_STORE);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).put({ group, fetchedAt, catalog });
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch {
    // ignore
  }
}

async function ensureIssInCatalog(catalog: Satellite[], signal?: AbortSignal): Promise<Satellite[]> {
  if (catalog.some((s) => s.noradId === ISS_NORAD_ID)) return catalog;
  try {
    const res = await fetchSatellites('stations', signal);
    const iss = res.catalog.find((s) => s.noradId === ISS_NORAD_ID);
    if (iss) return [...catalog, iss];
  } catch {
    // stations group unavailable
  }
  return catalog;
}

async function mergeFallbackGroups(signal?: AbortSignal): Promise<SatellitesResponse> {
  const results = await Promise.all(FALLBACK_GROUPS.map((g) => fetchSatellites(g, signal)));
  const merged: Satellite[] = [];
  const seen = new Set<string>();
  for (const res of results) {
    for (const sat of res.catalog) {
      if (seen.has(sat.noradId)) continue;
      seen.add(sat.noradId);
      merged.push(sat);
    }
  }
  const fetchedAt = Math.max(...results.map((r) => r.fetchedAt));
  return {
    fetchedAt,
    catalog: merged,
    fromCache: results.every((r) => r.fromCache),
    partial: results.some((r) => r.partial),
    error: merged.length === 0 ? results.find((r) => r.error)?.error : undefined,
  };
}

export function useSatelliteCatalog(enabled: boolean): SatelliteCatalogState {
  const catalogRef = useRef<Satellite[]>([]);
  const positionsRef = useRef<Map<string, SatellitePositionRaw>>(new Map());
  const inFlightRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);

  const [data, setData] = useState<SatellitesResponse | null>(null);
  const [satellites, setSatellites] = useState<RichSatellite[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [catalogAgeSec, setCatalogAgeSec] = useState<number | null>(null);

  const applyPositions = useCallback((positions: SatellitePositionRaw[]) => {
    const map = new Map<string, SatellitePositionRaw>();
    for (const p of positions) map.set(p.noradId, p);
    positionsRef.current = map;
    setSatellites(enrichSatellites(catalogRef.current, map));
  }, []);

  const propagateNow = useCallback(() => {
    if (!catalogRef.current.length) return;
    applyPositions(propagateCatalogRaw(catalogRef.current, new Date()));
  }, [applyPositions]);

  const loadCatalog = useCallback(async () => {
    if (!enabled) return;
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    const hasCatalog = catalogRef.current.length > 0;
    if (!hasCatalog) setLoading(true);
    else setRefreshing(true);

    try {
      const cached = await readIdbCache(PRIMARY_GROUP);
      if (cached && !ctrl.signal.aborted) {
        const withIss = await ensureIssInCatalog(cached.catalog, ctrl.signal);
        catalogRef.current = withIss;
        setData({ fetchedAt: cached.fetchedAt, catalog: withIss, fromCache: true });
        setCatalogAgeSec(Math.floor((Date.now() - cached.fetchedAt * 1000) / 1000));
        propagateNow();
        if (cached.stale) {
          fetchSatellites(PRIMARY_GROUP, ctrl.signal)
            .then(async (fresh) => {
              if (ctrl.signal.aborted || fresh.catalog.length === 0) return;
              catalogRef.current = fresh.catalog;
              setData(fresh);
              setCatalogAgeSec(0);
              propagateNow();
              await writeIdbCache(PRIMARY_GROUP, fresh.fetchedAt, fresh.catalog);
            })
            .catch(() => {});
        }
        return;
      }

      let res: SatellitesResponse;
      try {
        res = await fetchSatellites(PRIMARY_GROUP, ctrl.signal);
      } catch {
        res = await mergeFallbackGroups(ctrl.signal);
      }

      if (ctrl.signal.aborted) return;
      if (res.partial && res.catalog.length === 0) {
        setError(res.error ?? 'Satellite catalog temporarily unavailable');
        return;
      }

      const withIss = await ensureIssInCatalog(res.catalog, ctrl.signal);
      catalogRef.current = withIss;
      setData({ ...res, catalog: withIss });
      setCatalogAgeSec(Math.floor((Date.now() - res.fetchedAt * 1000) / 1000));
      setError(null);
      propagateNow();
      if (res.catalog.length > 0) await writeIdbCache(PRIMARY_GROUP, res.fetchedAt, res.catalog);
    } catch (err) {
      if (ctrl.signal.aborted) return;
      setError(apiErrorMessage(err));
    } finally {
      inFlightRef.current = false;
      if (abortRef.current === ctrl) abortRef.current = null;
      setLoading(false);
      setRefreshing(false);
    }
  }, [enabled, propagateNow]);

  const fetchOrbit = useCallback(async (noradId: string) => {
    const sat = catalogRef.current.find((s) => s.noradId === noradId);
    if (!sat) return [];
    return propagateOrbitPoints(sat, new Date());
  }, []);

  useEffect(() => {
    if (!enabled) {
      catalogRef.current = [];
      positionsRef.current = new Map();
      setSatellites([]);
      setData(null);
      setLoading(false);
      setRefreshing(false);
      setError(null);
      return;
    }

    void loadCatalog();
    const catId = setInterval(() => {
      if (!inFlightRef.current && !document.hidden) void loadCatalog();
    }, CATALOG_POLL_MS);
    const posId = setInterval(() => {
      if (!document.hidden) propagateNow();
    }, PROPAGATE_MS);

    return () => {
      clearInterval(catId);
      clearInterval(posId);
      abortRef.current?.abort();
    };
  }, [enabled, loadCatalog, propagateNow]);

  return {
    data,
    satellites,
    catalog: catalogRef.current,
    loading,
    refreshing,
    error,
    catalogAgeSec,
    refresh: loadCatalog,
    fetchOrbit,
  };
}
